/**
 * Tipos para DERAViariaR2Service — Red viaria DERA 09_01
 *
 * Define interfaces para los datos de carreteras andaluzas servidos por Worker R2.
 * Formato R2: roads agrupadas por código, cada una con segmentos [x,y] UTM30.
 * La capa WFS NO tiene cod_mun; la asignación municipal se resuelve
 * por proximidad al centroide municipal (CENTROIDES_MUNICIPIOS).
 *
 * Fuente: DERA G09 Transporte — IECA (Junta de Andalucía)
 * CRS: EPSG:25830 (UTM30 ETRS89)
 * URL Worker: /v1/dera/viaria  (sin .json.gz, el Worker hace mapping)
 * Key R2:     dera/viaria.json.gz  (sin prefijo v1/)
 *
 * @module types/deraViaria
 * @version 2.0.0
 * @date 2026-02-18
 */

// =============================================================================
// FORMATO RAW R2 (tal cual viene del Worker)
// =============================================================================

/** Carretera agrupada con todos sus tramos (formato R2) */
export interface ViariaRawRoad {
  /** Nombre descriptivo: "DE ORGIVA A UGIJAR" */
  nombre: string;
  /** Tipo: C=convencional, A=autovía, P=autopista, R=ronda, O=otro */
  tipo: string;
  /** Jerarquía: 1=primer orden, 2=segundo, 3=tercero, S=sin clasificar */
  jerarquia: string;
  /** Titularidad: E=estado, J=junta, D=diputación, M=municipal, O=otro */
  titular: string;
  /** Centroides de cada tramo [x, y] en EPSG:25830 */
  segs: [number, number][];
}

/** Dataset completo viaria (formato R2) */
export interface ViariaRawData {
  version: string;
  created: string;
  source: string;
  totalSegments: number;
  totalRoads: number;
  roads: Record<string, ViariaRawRoad>;
}

// =============================================================================
// OPCIONES DE BÚSQUEDA
// =============================================================================

/** Opciones para búsqueda por código de carretera */
export interface ViariaSearchByCodeOptions {
  /** Código de carretera: "A-348", "AL-3400", "N-340" */
  code: string;
  /** Código INE municipio (5 dígitos). Filtra segmento más cercano */
  codINE?: string;
  /** Máximo resultados (default: 5) */
  maxResults?: number;
}

/** Opciones para búsqueda por nombre de vía */
export interface ViariaSearchByNameOptions {
  /** Nombre o parte del nombre de la vía */
  name: string;
  /** Código INE municipio (5 dígitos). Filtra segmento más cercano */
  codINE?: string;
  /** Umbral mínimo confianza (0-100, default: 45) */
  minConfidence?: number;
  /** Máximo resultados (default: 5) */
  maxResults?: number;
}

// =============================================================================
// RESULTADO
// =============================================================================

/** Resultado de búsqueda viaria */
export interface DERAViariaResult {
  /** Centroide X UTM30 del segmento seleccionado */
  x: number;
  /** Centroide Y UTM30 del segmento seleccionado */
  y: number;
  /** Confianza (0-100): código exacto=65, parcial=55, fuzzy name=45-60 */
  confidence: number;
  /** Precisión estimada en metros (centroide de tramo) */
  precision: number;
  /** Fuente identificadora */
  source: 'dera_viaria_r2';
  /** Código de carretera */
  roadCode: string;
  /** Datos completos de la carretera */
  road: ViariaRawRoad;
  /** Tipo de match */
  matchType: 'code_exact' | 'code_partial' | 'name_fuzzy';
  /** Distancia al centroide municipal (m), si se filtró por codINE */
  distToMuniCentroid?: number;
  /** Score fuzzy (0-1, si aplica) */
  fuzzyScore?: number;
}

// =============================================================================
// REGEX CÓDIGOS DE CARRETERA ESPAÑOLES
// =============================================================================

/**
 * Patrón para códigos de carreteras españolas.
 * Nacionales: N-340, Autopistas: AP-7, Autovías: A-92,
 * Autonómicas: A-348, Comarcales: AL-3400, GR-3100
 */
export const ROAD_CODE_REGEX =
  /^(AP|A|N|E|R|AL|CA|CO|GR|HU|JA|MA|SE)-\d{1,5}[A-Z]?$/i;

/**
 * Patrón más permisivo para detectar códigos en texto libre.
 * Captura: "A-348", "la AL-3400", "carretera N-340"
 */
export const ROAD_CODE_IN_TEXT_REGEX =
  /\b((?:AP|A|N|E|R|AL|CA|CO|GR|HU|JA|MA|SE)-\d{1,5}[A-Z]?)\b/i;

// =============================================================================
// CONSTANTES
// =============================================================================

/** Precisión estimada centroide de tramo municipal (metros) */
export const VIARIA_CENTROID_PRECISION_M = 1500;

/** Confianza para match por código exacto */
export const VIARIA_CODE_EXACT_CONFIDENCE = 65;

/** Confianza para match por código parcial */
export const VIARIA_CODE_PARTIAL_CONFIDENCE = 55;

/** Confianza máxima para match por nombre fuzzy */
export const VIARIA_NAME_FUZZY_MAX_CONFIDENCE = 60;

/** Rangos válidos UTM30 Andalucía */
export const VIARIA_X_RANGE = { min: 100_000, max: 700_000 };
export const VIARIA_Y_RANGE = { min: 3_950_000, max: 4_300_000 };

// =============================================================================
// MAPAS DE DECODIFICACIÓN (char compacto → nombre legible)
// =============================================================================

export const TIPO_MAP: Record<string, string> = {
  C: 'Convencional', A: 'Autovía', P: 'Autopista', R: 'Ronda', O: 'Otro',
};

export const TITULAR_MAP: Record<string, string> = {
  E: 'Estado', J: 'Junta de Andalucía', D: 'Diputación', M: 'Municipal', O: 'Otro',
};
